import { useEffect, useState } from "react";
import { Check, Mail, MessageCircleMore, Save, Smartphone } from "lucide-react";
import { SuiteUserProfile } from "@tribunalea/auth/react";
import { PageHeader } from "@tribunalea/ui";
import { useApi, useApp } from "../App.jsx";

const blankProfile = {
  fullName: "",
  email: "",
  phoneE164: "",
  whatsappE164: "",
  legalSignature: {
    imageDataUrl: "",
    imageWidth: null,
    imageHeight: null,
    displayName: "",
    role: "",
    registration: "",
    alignment: "left",
  },
  consents: {
    email: { enabled: true },
    sms: { enabled: false },
    whatsapp: { enabled: false },
  },
};

function normalizeProfile(data = {}) {
  return {
    fullName: data.fullName || "",
    email: data.email || "",
    phoneE164: data.phoneE164 || "",
    whatsappE164: data.whatsappE164 || "",
    legalSignature: {
      ...blankProfile.legalSignature,
      ...data.legalSignature,
    },
    consents: {
      email: {
        ...blankProfile.consents.email,
        ...data.consents?.email,
      },
      sms: {
        ...blankProfile.consents.sms,
        ...data.consents?.sms,
      },
      whatsapp: {
        ...blankProfile.consents.whatsapp,
        ...data.consents?.whatsapp,
      },
    },
  };
}

export function ProfilePage() {
  const api = useApi();
  const { authEnabled } = useApp();
  const [profile, setProfile] = useState(blankProfile);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.profile().then((data) => data && setProfile(normalizeProfile(data)));
  }, []);

  const updateConsent = (channel, enabled) => {
    setProfile((current) => ({
      ...current,
      consents: {
        ...current.consents,
        [channel]: { ...current.consents[channel], enabled },
      },
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const updated = await api.updateProfile(profile);
      setProfile(normalizeProfile(updated));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="page profile-page" onSubmit={submit}>
      <PageHeader
        eyebrow="Administración"
        title="Mi perfil"
        description="Estos datos se usan para enviar tus alertas de Praxis."
        actions={(
          <button className="button primary" disabled={saving}>
            {saved ? <Check size={18} /> : <Save size={18} />}
            {saved ? "Guardado" : saving ? "Guardando…" : "Guardar cambios"}
          </button>
        )}
      />

      {error && <div className="inline-error">{error}</div>}

      <section className="profile-card">
        <div className="profile-card-heading">
          <span className="member-avatar profile-avatar">
            {(profile.fullName || "Usuario")
              .split(" ")
              .slice(0, 2)
              .map((part) => part[0])
              .join("")}
          </span>
          <span>
            <h2>Datos de contacto</h2>
            <p>Mantenelos actualizados para no perder avisos importantes.</p>
          </span>
        </div>
        <div className="profile-grid">
          <label className="field">
            <span>Nombre completo</span>
            <input
              value={profile.fullName}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  fullName: event.target.value,
                }))
              }
              required
            />
          </label>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={profile.email}
              onChange={(event) =>
                setProfile((current) => ({ ...current, email: event.target.value }))
              }
              required
            />
          </label>
          <label className="field">
            <span>Teléfono para SMS</span>
            <input
              value={profile.phoneE164}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  phoneE164: event.target.value,
                }))
              }
              placeholder="+5492615550192"
            />
            <small>Formato internacional, sin espacios.</small>
          </label>
          <label className="field">
            <span>Teléfono para WhatsApp</span>
            <input
              value={profile.whatsappE164}
              onChange={(event) =>
                setProfile((current) => ({
                  ...current,
                  whatsappE164: event.target.value,
                }))
              }
              placeholder="+5492615550192"
            />
            <small>Puede ser el mismo número.</small>
          </label>
        </div>
      </section>

      <section className="profile-card consent-card">
        <div className="section-title">
          <h2>Canales habilitados</h2>
          <p>Podés retirar tu consentimiento en cualquier momento.</p>
        </div>
        {[
          ["email", "Email", "Avisos a tu correo principal", Mail],
          ["sms", "SMS", "Mensajes de texto al teléfono indicado", Smartphone],
          [
            "whatsapp",
            "WhatsApp",
            "Mensajes desde la cuenta verificada de Tribunalea",
            MessageCircleMore,
          ],
        ].map(([channel, label, description, Icon]) => (
          <label className="consent-row" key={channel}>
            <span className="option-icon">
              <Icon size={18} />
            </span>
            <span>
              <strong>{label}</strong>
              <small>{description}</small>
            </span>
            <input
              type="checkbox"
              checked={profile.consents[channel]?.enabled || false}
              onChange={(event) => updateConsent(channel, event.target.checked)}
            />
            <span className="switch" aria-hidden="true" />
          </label>
        ))}
      </section>

      {authEnabled && (
        <section className="account-security-section">
          <div className="section-title">
            <h2>Cuenta y seguridad</h2>
            <p>
              Administrá tu email de acceso, contraseña, sesiones y conexiones
              sociales.
            </p>
          </div>
          <div className="clerk-user-profile-surface">
            <SuiteUserProfile />
          </div>
        </section>
      )}
    </form>
  );
}
